import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import type { GetUserContributorsDto } from '@/types/dtos/contributors/response/get-user-contributors.dto';
import type { GetUserContributorsListResponseDto } from '@/types/dtos/contributors/response/get-user-contributors-list.dto';
import type { ContributorSummaryDto } from '@/types/dtos/contributors/response/contributor-summary.dto';

export const GET_USER_CONTRIBUTORS_QUERY_KEY = 'get-user-contributors-query-key';
export const GET_USER_CONTRIBUTORS_LIST_QUERY_KEY = 'get-user-contributors-list-query-key';
export const GET_CONTRIBUTOR_SUMMARY_QUERY_KEY = 'get-contributor-summary-query-key';
const PAGE_SIZE = 50;

export const useUserContributorsQuery = () => {
  const { getToken } = useAuth();

  return useInfiniteQuery({
    queryKey: [GET_USER_CONTRIBUTORS_QUERY_KEY],
    queryFn: async ({ pageParam }: { pageParam: string }) => {
      const token = await getToken();
      return getUserContributors({ pageParam, token: token ?? '' });
    },
    initialPageParam: '' as string,
    getNextPageParam: (lastPage) => lastPage.pagination.nextCursor,
  });
};

const getUserContributors = async ({
  pageParam,
  token,
}: {
  pageParam: string;
  token: string;
}): Promise<GetUserContributorsDto> => {
  let url = `${import.meta.env.VITE_API_URL}/contributors/user?limit=${PAGE_SIZE}`;
  if (pageParam !== '') {
    url += `&cursor=${pageParam}`;
  }
  return sendJSONRequest({ url, method: METHOD.GET, token });
};

export const useUserContributorsListQuery = () => {
  const { getToken } = useAuth();

  return useInfiniteQuery({
    queryKey: [GET_USER_CONTRIBUTORS_LIST_QUERY_KEY],
    queryFn: async ({ pageParam }: { pageParam: string }) => {
      const token = await getToken();
      return getUserContributorsList({ pageParam, token: token ?? '' });
    },
    initialPageParam: '' as string,
    getNextPageParam: (lastPage) => lastPage.pagination.nextCursor,
  });
};

const getUserContributorsList = async ({
  pageParam,
  token,
}: {
  pageParam: string;
  token: string;
}): Promise<GetUserContributorsListResponseDto> => {
  let url = `${import.meta.env.VITE_API_URL}/contributors/user/list?limit=${PAGE_SIZE}`;
  if (pageParam !== '') {
    url += `&cursor=${pageParam}`;
  }
  return sendJSONRequest({ url, method: METHOD.GET, token });
};

export const useContributorSummaryQuery = (contributorId: string | undefined) => {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: [GET_CONTRIBUTOR_SUMMARY_QUERY_KEY, contributorId],
    queryFn: async () => {
      if (!contributorId) return undefined;
      const token = await getToken();
      return getContributorSummary({ contributorId, token: token ?? '' });
    },
    enabled: !!contributorId,
  });
};

const getContributorSummary = async ({
  contributorId,
  token,
}: {
  contributorId: string;
  token: string;
}): Promise<ContributorSummaryDto> => {
  const url = `${import.meta.env.VITE_API_URL}/contributors/${contributorId}`;
  return sendJSONRequest({ url, method: METHOD.GET, token });
};
